import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
import { getEffectiveUserId } from '../utils/auth-session-utils';
import { handleApiError } from '../utils/api-helpers';
import axios from 'axios';
import { getSubjectById } from '../constants/subjects';
/** Returned when an older in-flight list fetch finishes after a newer one started — reducer must ignore (prevents stale overwrite). */
const CLASSROOMS_LIST_FETCH_SUPERSEDED = 'CLASSROOMS_LIST_FETCH_SUPERSEDED';
let classroomsByTutorFetchSeq = 0;
function mapApiClassroomRow(classroom) {
    const subject = getSubjectById(classroom.subject_id);
    const subject_name = classroom.subject_name || subject?.name || 'Unknown Subject';
    return {
        id: classroom.id,
        name: classroom.name,
        tutor_id: classroom.tutor_id,
        subject_id: classroom.subject_id,
        subject_name,
        education_system_id: classroom.education_system_id,
        education_system_name: classroom.education_system_name || 'Not Set',
        education_system_code: classroom.education_system_code,
        academic_level_id: classroom.academic_level_id,
        academic_level_name: classroom.academic_level_name || 'Not Set',
        academic_level_code: classroom.academic_level_code,
        max_students: classroom.max_students,
        students: [],
        pending_enrollments: [],
        lessons: [],
        quizzes: [],
        created_at: classroom.created_at,
        updated_at: classroom.updated_at,
        student_count: classroom.student_count || 0,
        lessons_count: classroom.lessons_count || 0,
        quizzes_count: classroom.quizzes_count || 0,
    };
}
// Create classroom thunk
export const create_classroom = createAsyncThunk('classroom/create_classroom', async (classroom_data, { rejectWithValue, getState }) => {
    try {
        // Get current user ID from state
        const state = getState();
        const userId = state.auth?.user?.id;
        if (!userId) {
            return rejectWithValue('User not authenticated');
        }
        // Enforce maximum of 20 students and include tutorId
        const data = {
            name: classroom_data.name,
            tutorId: userId, // Add the required tutorId field
            subjectId: classroom_data.subject_id, // Map subject_id to subjectId
            educationSystemId: classroom_data.education_system_id, // Add education system ID
            academicLevelId: classroom_data.academic_level_id, // Add academic level ID
            maxStudents: Math.min(classroom_data.max_students, 20), // Map max_students to maxStudents
            settings: {}, // Add default settings
            metadata: {} // Add default metadata
        };
        const response = await axiosInstance.post('/classrooms', data);
        const envelope = response.data;
        return envelope.data ?? response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to create classroom');
    }
});
// Get classrooms by tutor thunk
export const get_classrooms_by_tutor = createAsyncThunk('classroom/get_classrooms_by_tutor', async (_, { rejectWithValue, getState }) => {
    const seq = ++classroomsByTutorFetchSeq;
    try {
        const state = getState();
        const userRole = state.auth?.user?.role;
        const userId = getEffectiveUserId(state.auth?.user?.id);
        if (!userId) {
            console.error('User not authenticated or ID not available');
            return rejectWithValue('User not authenticated');
        }
        const effectiveRole = userRole || 'tutor';
        let url;
        if (effectiveRole === 'tutor') {
            url = `/classrooms/tutor/${userId}`;
        }
        else if (effectiveRole === 'student') {
            url = `/classrooms/student/${userId}`;
        }
        else {
            console.error('Unknown user role:', effectiveRole);
            return rejectWithValue('Unknown user role');
        }
        const response = await axiosInstance.get(url);
        if (seq !== classroomsByTutorFetchSeq) {
            return rejectWithValue(CLASSROOMS_LIST_FETCH_SUPERSEDED);
        }
        const raw = response.data?.data ??
            response.data;
        const list = Array.isArray(raw) ? raw : [];
        return list.map((row) => mapApiClassroomRow(row));
    }
    catch (apiError) {
        if (seq !== classroomsByTutorFetchSeq) {
            return rejectWithValue(CLASSROOMS_LIST_FETCH_SUPERSEDED);
        }
        console.error('Error using API endpoint for classrooms:', apiError);
        const msg = axios.isAxiosError(apiError)
            ? apiError.response?.data?.message ||
                apiError.message
            : apiError?.message || 'Failed to fetch classrooms';
        return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to fetch classrooms');
    }
});
// Get classrooms a student has enrolled in
export const get_student_enrolled_classrooms = createAsyncThunk('classroom/get_student_enrolled_classrooms', async (_, { rejectWithValue, getState }) => {
    try {
        // Import Supabase client and environment variables
        const { default: supabase } = await import('../utils/supabase-client');
        // Get user ID from state
        const state = getState();
        const userId = state.auth?.user?.id;
        if (!userId) {
            console.error('User not authenticated or ID not available');
            return [];
        }
        // First, try using the enhanced student classroom display function
        try {
            const { data, error } = await supabase.rpc('get_student_enrolled_classrooms_display_data', {
                p_student_id: userId
            });
            if (!error && data) {
                // Transform the data to match our Classroom interface
                const transformedData = data.map((classroom) => {
                    // Use static constants as fallback for subject names
                    const subject = getSubjectById(classroom.subject_id);
                    const subject_name = classroom.subject_name || subject?.name || 'Unknown Subject';
                    return {
                        id: classroom.id,
                        name: classroom.name,
                        tutor_id: classroom.tutor_id,
                        subject_id: classroom.subject_id,
                        subject_name: subject_name, // Use fallback
                        education_system_id: classroom.education_system_id,
                        education_system_name: classroom.education_system_name || 'Not Set',
                        education_system_code: classroom.education_system_code,
                        academic_level_id: classroom.academic_level_id,
                        academic_level_name: classroom.academic_level_name || 'Not Set',
                        academic_level_code: classroom.academic_level_code,
                        max_students: classroom.max_students,
                        students: [], // Will be populated separately if needed
                        pending_enrollments: [], // Will be populated separately if needed
                        lessons: [], // Will be populated separately if needed
                        quizzes: [], // Will be populated separately if needed
                        created_at: classroom.created_at,
                        updated_at: classroom.updated_at,
                        // Add the counts as properties for easy access
                        student_count: classroom.student_count || 0,
                        lessons_count: classroom.lessons_count || 0,
                        quizzes_count: classroom.quizzes_count || 0
                    };
                });
                return transformedData;
            }
            if (error) {
                console.error('Enhanced student classroom fetch failed:', error);
            }
        }
        catch (clientError) {
            console.error('Error using enhanced student classroom function:', clientError);
        }
        // Fallback to using RLS to get classrooms the student is enrolled in
        try {
            // With proper RLS, this will automatically filter to classrooms the student is enrolled in
            const { data, error } = await supabase
                .from('classrooms')
                .select('*')
                .returns();
            if (!error && data) {
                // Transform data to include subject names from constants
                const transformedData = data.map((classroom) => {
                    const subject = getSubjectById(classroom.subject_id);
                    const subject_name = subject?.name || 'Unknown Subject';
                    return {
                        ...classroom,
                        subject_name: subject_name,
                        education_system_name: classroom.education_system_name || 'Not Set',
                        academic_level_name: classroom.academic_level_name || 'Not Set',
                        student_count: classroom.student_count || 0,
                        lessons_count: classroom.lessons_count || 0,
                        quizzes_count: classroom.quizzes_count || 0
                    };
                });
                return transformedData;
            }
            if (error) {
                console.error('Error using RLS to fetch classrooms:', error);
            }
        }
        catch (error) {
            console.error('Error with direct RLS classroom query:', error);
        }
        // Fallback to explicit join if RLS doesn't work as expected
        try {
            const { data, error } = await supabase
                .from('classroom_students')
                .select('classroom:classrooms(*)')
                .eq('student_id', userId)
                .eq('status', 'active') // Only get active enrollments
                .returns();
            if (error) {
                console.error('Error with explicit join query:', error);
                return [];
            }
            // Extract classroom data from join results
            const classrooms = data
                .map(item => item.classroom)
                .filter(classroom => classroom !== null);
            // Transform data to include subject names from constants
            const transformedClassrooms = classrooms.map((classroom) => {
                const subject = getSubjectById(classroom.subject_id);
                const subject_name = subject?.name || 'Unknown Subject';
                return {
                    ...classroom,
                    subject_name: subject_name,
                    education_system_name: classroom.education_system_name || 'Not Set',
                    academic_level_name: classroom.academic_level_name || 'Not Set',
                    student_count: classroom.student_count || 0,
                    lessons_count: classroom.lessons_count || 0,
                    quizzes_count: classroom.quizzes_count || 0
                };
            });
            return transformedClassrooms;
        }
        catch (error) {
            console.error('Error with fallback join query:', error);
            return [];
        }
    }
    catch (error) {
        console.error('Error fetching student enrollments:', error);
        return [];
    }
});
// Get pending classroom join requests for a student
export const get_student_pending_requests = createAsyncThunk('classroom/get_student_pending_requests', async (_, { rejectWithValue, getState }) => {
    try {
        // Import Supabase client
        const { default: supabase } = await import('../utils/supabase-client');
        // Get user ID from state
        const state = getState();
        const userId = state.auth?.user?.id;
        if (!userId) {
            console.error('User not authenticated or ID not available');
            return [];
        }
        // Get pending enrollments for this student
        const { data, error } = await supabase
            .from('classroom_students')
            .select('*, classroom:classrooms(*)')
            .eq('student_id', userId)
            .eq('status', 'pending')
            .returns();
        if (error) {
            console.error('Error fetching pending enrollment requests:', error);
            return rejectWithValue(error.message);
        }
        return data || [];
    }
    catch (error) {
        console.error('Error fetching pending requests:', error);
        return rejectWithValue(error.message);
    }
});
// Get pending enrollment requests for a classroom (for tutors)
export const get_classroom_pending_requests = createAsyncThunk('classroom/get_classroom_pending_requests', async (classroom_id, { rejectWithValue, getState }) => {
    try {
        // Import Supabase client
        const { default: supabase } = await import('../utils/supabase-client');
        // Get user role to validate access
        const state = getState();
        const userRole = state.auth?.user?.role;
        const userId = state.auth?.user?.id;
        // Only tutors and admins can view pending requests
        if (userRole !== 'tutor' && userRole !== 'admin') {
            return rejectWithValue('Only tutors and admins can view pending enrollment requests');
        }
        // For tutors, verify they own the classroom if not admin
        if (userRole === 'tutor') {
            const { data: classroom, error: classroomError } = await supabase
                .from('classrooms')
                .select('tutor_id')
                .eq('id', classroom_id)
                .single();
            if (classroomError || !classroom) {
                return rejectWithValue('Classroom not found');
            }
            if (classroom.tutor_id !== userId) {
                return rejectWithValue('You can only view pending requests for your own classrooms');
            }
        }
        // Get pending enrollments for this classroom with student details
        const { data, error } = await supabase
            .from('classroom_students')
            .select('*, student:students(*)')
            .eq('classroom_id', classroom_id)
            .eq('status', 'pending')
            .returns();
        if (error) {
            console.error('Error fetching classroom pending requests:', error);
            return rejectWithValue(error.message);
        }
        // Format the response to match our StudentEnrollment interface
        const formattedRequests = data.map(item => ({
            ...item,
            student: item.student
        }));
        return formattedRequests || [];
    }
    catch (error) {
        console.error('Error fetching classroom pending requests:', error);
        return rejectWithValue(error.message);
    }
});
// Update student enrollment status (approve/reject/suspend)
export const update_enrollment_status = createAsyncThunk('classroom/update_enrollment_status', async ({ classroom_id, student_id, status }, { rejectWithValue, getState }) => {
    try {
        // Import Supabase client
        const { default: supabase } = await import('../utils/supabase-client');
        // Get user role to validate access
        const state = getState();
        const userRole = state.auth?.user?.role;
        const userId = state.auth?.user?.id;
        // Only tutors and admins can update enrollment status
        if (userRole !== 'tutor' && userRole !== 'admin') {
            return rejectWithValue('Only tutors and admins can update enrollment status');
        }
        // If activating a student, check classroom capacity
        if (status === 'active') {
            const { data: classroom, error: classroomError } = await supabase
                .from('classrooms')
                .select('*, classroom_students!inner(status)')
                .eq('id', classroom_id)
                .single();
            if (classroomError || !classroom) {
                return rejectWithValue('Classroom not found');
            }
            // Count current active students
            const activeCount = (classroom.classroom_students || [])
                .filter((cs) => cs.status === 'active')
                .length;
            if (activeCount >= classroom.max_students) {
                return rejectWithValue('Cannot activate student. Classroom has reached maximum capacity');
            }
        }
        // Update the enrollment status
        const { data, error } = await supabase
            .from('classroom_students')
            .update({ status })
            .eq('classroom_id', classroom_id)
            .eq('student_id', student_id)
            .select()
            .returns();
        if (error) {
            console.error('Error updating enrollment status:', error);
            return rejectWithValue(error.message);
        }
        if (!data || data.length === 0) {
            return rejectWithValue('Enrollment not found');
        }
        return {
            student_id,
            classroom_id,
            status
        };
    }
    catch (error) {
        console.error('Error updating enrollment status:', error);
        return rejectWithValue(error.message);
    }
});
// Add student to classroom thunk
export const add_student_to_classroom = createAsyncThunk('classroom/add_student', async ({ classroom_id, student_id }, { rejectWithValue, getState }) => {
    try {
        // Import Supabase client
        const { default: supabase } = await import('../utils/supabase-client');
        // Get user state including role
        const state = getState();
        const userRole = state.auth?.user?.role;
        const userId = state.auth?.user?.id;
        // For student self-enrollment, enforce they can only enroll themselves
        if (userRole === 'student' && student_id !== userId) {
            return rejectWithValue('Students can only request to join classes for themselves');
        }
        // Verify classroom exists and has capacity
        const { data: classroom, error: classroomError } = await supabase
            .from('classrooms')
            .select('*, classroom_students(status)')
            .eq('id', classroom_id)
            .single();
        if (classroomError || !classroom) {
            console.error('Error fetching classroom for enrollment:', classroomError);
            return rejectWithValue('Classroom not found');
        }
        // Count active students
        const activeStudents = classroom.classroom_students?.filter((cs) => cs.status === 'active') || [];
        if (activeStudents.length >= classroom.max_students) {
            return rejectWithValue('Classroom has reached maximum capacity');
        }
        // Create a new enrollment request with pending status
        const { data, error } = await supabase
            .from('classroom_students')
            .insert({
            classroom_id,
            student_id,
            status: 'pending' // Default is pending as per new schema
        })
            .select()
            .returns();
        if (error) {
            console.error('Error adding student to classroom:', error);
            return rejectWithValue(error.message);
        }
        // Get student details using RPC function
        const { data: studentData, error: studentError } = await supabase
            .rpc('get_user_profile_by_id', {
            user_id: student_id,
            user_role: 'student'
        });
        if (studentError || !studentData) {
            console.error('Error fetching student details:', studentError);
            return data[0]; // Return enrollment without student details
        }
        // Return enrollment with student details
        return {
            ...data[0],
            student: studentData
        };
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to add student to classroom');
    }
});
// Soft delete student from classroom thunk
export const soft_delete_student_from_classroom = createAsyncThunk('classroom/soft_delete_student', async ({ classroom_id, student_id }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/classrooms/${classroom_id}/students/${student_id}/soft-delete`);
        const data = response.data;
        return {
            success: data.success,
            student_id,
            classroom_id
        };
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Restore student to classroom thunk
export const restore_student_to_classroom = createAsyncThunk('classroom/restore_student', async ({ classroom_id, student_id }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/classrooms/${classroom_id}/students/${student_id}/restore`);
        const data = response.data;
        return {
            success: data.success,
            student_id,
            classroom_id
        };
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Get soft-deleted students thunk
export const get_soft_deleted_students = createAsyncThunk('classroom/get_soft_deleted_students', async (classroom_id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/classrooms/${classroom_id}/students/soft-deleted`);
        const envelope = response.data;
        return envelope.data ?? [];
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Add lesson to classroom thunk
export const add_lesson_to_classroom = createAsyncThunk('classroom/add_lesson', async ({ classroom_id, title, description, date, materials }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/classrooms/${classroom_id}/lessons`, {
            title,
            description,
            date: date.toISOString(), // Convert to ISO format for backend
            materials
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to add lesson to classroom');
    }
});
// Add quiz to classroom thunk
export const add_quiz_to_classroom = createAsyncThunk('classroom/add_quiz', async ({ classroom_id, title, description, due_date, questions }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/classrooms/${classroom_id}/quizzes`, {
            title,
            description,
            due_date: due_date.toISOString(), // Convert to ISO format for backend
            questions
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to add quiz to classroom');
    }
});
// Get classroom by ID thunk
export const get_classroom_by_id = createAsyncThunk('classroom/get_classroom_by_id', async (classroom_id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/classrooms/${classroom_id}`);
        const envelope = response.data;
        const data = envelope.data ?? response.data;
        if (!data)
            throw new Error('No classroom data in response');
        return data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch classroom');
    }
});
// Associate lesson with classroom thunk
export const associate_lesson_with_classroom = createAsyncThunk('classroom/associate_lesson', async ({ lessonId, classroomId, visibility_rule, visible_from, unlock_after_lesson_id }, { rejectWithValue }) => {
    try {
        const body = { lessonId };
        if (visibility_rule)
            body.visibility_rule = visibility_rule;
        if (visible_from)
            body.visible_from = visible_from;
        if (unlock_after_lesson_id)
            body.unlock_after_lesson_id = unlock_after_lesson_id;
        const response = await axiosInstance.post(`/classrooms/${classroomId}/lessons`, body);
        const envelope = response.data;
        if (envelope && envelope.success === false) {
            return rejectWithValue(envelope.message || 'Failed to associate lesson with classroom');
        }
        return response.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error) || 'Failed to associate lesson with classroom');
    }
});
// Disassociate lesson from classroom thunk
export const disassociate_lesson_from_classroom = createAsyncThunk('classroom/disassociate_lesson', async ({ lessonId, classroomId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/classrooms/${classroomId}/lessons/${lessonId}`);
        const envelope = response.data;
        if (envelope && envelope.success === false) {
            return rejectWithValue(envelope.message || 'Failed to disassociate lesson from classroom');
        }
        return response.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error) || 'Failed to disassociate lesson from classroom');
    }
});
// Associate quiz with classroom thunk
export const associate_quiz_with_classroom = createAsyncThunk('classroom/associate_quiz', async ({ quizId, classroomId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/classrooms/${classroomId}/quizzes`, {
            quizId
        });
        const envelope = response.data;
        if (envelope && envelope.success === false) {
            return rejectWithValue(envelope.message || 'Failed to associate quiz with classroom');
        }
        return response.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error) || 'Failed to associate quiz with classroom');
    }
});
// Disassociate quiz from classroom thunk
export const disassociate_quiz_from_classroom = createAsyncThunk('classroom/disassociate_quiz', async (quizId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/quizzes/${quizId}/classroom`);
        const envelope = response.data;
        if (envelope && envelope.success === false) {
            return rejectWithValue(envelope.message || 'Failed to disassociate quiz from classroom');
        }
        return response.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error) || 'Failed to disassociate quiz from classroom');
    }
});
// Get classroom lessons thunk
export const get_classroom_lessons = createAsyncThunk('classroom/get_classroom_lessons', async (classroomId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/classrooms/${classroomId}/lessons`);
        const envelope = response.data;
        return envelope.data ?? [];
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch classroom lessons');
    }
});
// Get classroom quizzes thunk
export const get_classroom_quizzes = createAsyncThunk('classroom/get_classroom_quizzes', async (classroomId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/classrooms/${classroomId}/quizzes`);
        const envelope = response.data;
        return envelope.data ?? [];
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch classroom quizzes');
    }
});
// Get available lessons for tutor thunk
export const get_available_lessons = createAsyncThunk('classroom/get_available_lessons', async ({ classroomId }, { rejectWithValue }) => {
    try {
        const params = classroomId ? `?classroomId=${classroomId}` : '';
        const response = await axiosInstance.get(`/lessons/available${params}`);
        const envelope = response.data;
        return envelope.data ?? [];
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch available lessons');
    }
});
// Get available quizzes for tutor thunk
export const get_available_quizzes = createAsyncThunk('classroom/get_available_quizzes', async ({ classroomId }, { rejectWithValue }) => {
    try {
        const params = classroomId ? `?classroomId=${classroomId}` : '';
        const response = await axiosInstance.get(`/quizzes/available${params}`);
        const envelope = response.data;
        return envelope.data ?? [];
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch available quizzes');
    }
});
// Get classrooms for a lesson thunk
export const get_lesson_classrooms = createAsyncThunk('classroom/get_lesson_classrooms', async ({ lessonId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/lessons/${lessonId}/classrooms`);
        const envelope = response.data;
        return envelope.data ?? [];
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch lesson classrooms');
    }
});
// Set lesson visibility for a classroom
export const set_lesson_visibility = createAsyncThunk('classroom/set_lesson_visibility', async ({ lessonId, classroomId, visibilityRule, visibleFrom, unlockAfterLessonId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put(`/lessons/${lessonId}/visibility`, {
            classroom_id: classroomId,
            visibility_rule: visibilityRule,
            visible_from: visibleFrom,
            unlock_after_lesson_id: unlockAfterLessonId
        });
        const envelope = response.data;
        if (!envelope.success) {
            return rejectWithValue(envelope.message || 'Failed to set visibility');
        }
        return { success: true, message: envelope.message };
    }
    catch (error) {
        const msg = error?.response?.data?.message;
        return rejectWithValue(msg || error.message || 'Failed to set visibility');
    }
});
// Unlock lesson for a classroom (manual visibility)
export const unlock_lesson_for_classroom = createAsyncThunk('classroom/unlock_lesson_for_classroom', async ({ lessonId, classroomId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/lessons/${lessonId}/unlock`, {
            classroom_id: classroomId
        });
        const envelope = response.data;
        if (!envelope.success) {
            return rejectWithValue(envelope.message || 'Failed to unlock lesson');
        }
        return { success: true, message: envelope.message };
    }
    catch (error) {
        const msg = error?.response?.data?.message;
        return rejectWithValue(msg || error.message || 'Failed to unlock lesson');
    }
});
// Set lesson visibility in bulk (multiple classrooms and/or students)
export const set_lesson_visibility_bulk = createAsyncThunk('classroom/set_lesson_visibility_bulk', async ({ lessonId, targets, visibilityRule, visibleFrom }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/lessons/${lessonId}/visibility/bulk`, {
            targets,
            visibility_rule: visibilityRule,
            visible_from: visibilityRule === 'scheduled' ? visibleFrom : undefined
        });
        const envelope = response.data;
        if (!envelope.success) {
            return rejectWithValue(envelope.message || 'Failed to set visibility');
        }
        return { success: true, message: envelope.message, count: envelope.data?.count };
    }
    catch (error) {
        const msg = error?.response?.data?.message;
        return rejectWithValue(msg || error.message || 'Failed to set visibility');
    }
});
// Unlock lesson in bulk (multiple classrooms and/or students)
export const unlock_lesson_bulk = createAsyncThunk('classroom/unlock_lesson_bulk', async ({ lessonId, targets }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/lessons/${lessonId}/unlock/bulk`, { targets });
        const envelope = response.data;
        if (!envelope.success) {
            return rejectWithValue(envelope.message || 'Failed to unlock lesson');
        }
        return { success: true, message: envelope.message, count: envelope.data?.count };
    }
    catch (error) {
        const msg = error?.response?.data?.message;
        return rejectWithValue(msg || error.message || 'Failed to unlock lesson');
    }
});
// Get classroom IDs for multiple quiz templates in one batch request
export const get_quiz_template_classrooms_batch = createAsyncThunk('classroom/get_quiz_template_classrooms_batch', async ({ templateIds }, { rejectWithValue }) => {
    try {
        if (!templateIds?.length)
            return {};
        const ids = templateIds.slice(0, 100).join(',');
        const response = await axiosInstance.get(`/quiz-templates/classrooms/batch?ids=${encodeURIComponent(ids)}`);
        const envelope = response.data;
        return envelope.data ?? {};
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch quiz template classrooms batch');
    }
});
// Set quiz visibility rule for a quiz-classroom association
export const set_quiz_visibility = createAsyncThunk('classroom/set_quiz_visibility', async ({ quizTemplateId, classroomId, visibilityRule, visibleFrom, unlockAfterLessonId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/quiz-templates/${quizTemplateId}/visibility`, {
            classroomId,
            visibilityRule,
            visibleFrom,
            unlockAfterLessonId,
        });
        const envelope = response.data;
        if (!envelope.success) {
            return rejectWithValue(envelope.message || 'Failed to set quiz visibility');
        }
        return { success: true, message: envelope.message };
    }
    catch (error) {
        return rejectWithValue(handleApiError(error) || 'Failed to set quiz visibility');
    }
});
// Unlock quiz for classroom (make visible to students)
export const unlock_quiz_for_classroom = createAsyncThunk('classroom/unlock_quiz_for_classroom', async ({ quizTemplateId, classroomId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/quiz-templates/${quizTemplateId}/unlock`, {
            classroomId,
        });
        const envelope = response.data;
        if (!envelope.success) {
            return rejectWithValue(envelope.message || 'Failed to unlock quiz');
        }
        return { success: true, message: envelope.message };
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to unlock quiz');
    }
});
// Set quiz visibility in bulk (multiple classrooms and/or students)
export const set_quiz_visibility_bulk = createAsyncThunk('classroom/set_quiz_visibility_bulk', async ({ quizTemplateId, targets, visibilityRule, visibleFrom }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/quiz-templates/${quizTemplateId}/visibility/bulk`, {
            targets,
            visibility_rule: visibilityRule,
            visible_from: visibilityRule === 'scheduled' ? visibleFrom : undefined
        });
        const envelope = response.data;
        if (!envelope.success) {
            return rejectWithValue(envelope.message || 'Failed to set visibility');
        }
        return { success: true, message: envelope.message, count: envelope.data?.count };
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to set visibility');
    }
});
// Unlock quiz in bulk (multiple classrooms and/or students)
export const unlock_quiz_bulk = createAsyncThunk('classroom/unlock_quiz_bulk', async ({ quizTemplateId, targets }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/quiz-templates/${quizTemplateId}/unlock/bulk`, { targets });
        const envelope = response.data;
        if (!envelope.success) {
            return rejectWithValue(envelope.message || 'Failed to unlock quiz');
        }
        return { success: true, message: envelope.message, count: envelope.data?.count };
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to unlock quiz');
    }
});
// Set classrooms for a quiz template (replace existing associations)
export const set_quiz_template_classrooms = createAsyncThunk('classroom/set_quiz_template_classrooms', async ({ templateId, classroomIds }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put(`/quiz-templates/${templateId}/classrooms`, {
            classroomIds: Array.isArray(classroomIds) ? classroomIds : []
        });
        const envelope = response.data;
        return {
            success: envelope.success ?? true,
            data: envelope.data ?? [],
            message: envelope.message ?? 'Associations updated'
        };
    }
    catch (error) {
        return rejectWithValue(handleApiError(error) || 'Failed to set quiz template classrooms');
    }
});
// Get classrooms for multiple lessons in one batch request
export const get_lesson_classrooms_batch = createAsyncThunk('classroom/get_lesson_classrooms_batch', async ({ lessonIds }, { rejectWithValue }) => {
    try {
        if (!lessonIds?.length)
            return {};
        const ids = lessonIds.slice(0, 100).join(',');
        const response = await axiosInstance.get(`/lessons/classrooms/batch?lessonIds=${encodeURIComponent(ids)}`);
        const envelope = response.data;
        return envelope.data ?? {};
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch lesson classrooms batch');
    }
});
// Bulk associate multiple lessons with a classroom thunk
export const bulk_associate_lessons_with_classroom = createAsyncThunk('classroom/bulk_associate_lessons', async ({ lessonIds, classroomId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/classrooms/${classroomId}/lessons/bulk`, {
            lessonIds
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to bulk associate lessons with classroom');
    }
});
// Bulk disassociate multiple lessons from a classroom thunk
export const bulk_disassociate_lessons_from_classroom = createAsyncThunk('classroom/bulk_disassociate_lessons', async ({ lessonIds, classroomId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/classrooms/${classroomId}/lessons/bulk`, {
            data: { lessonIds }
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to bulk disassociate lessons from classroom');
    }
});
// Bulk associate multiple quizzes with a classroom thunk
export const bulk_associate_quizzes_with_classroom = createAsyncThunk('classroom/bulk_associate_quizzes', async ({ quizIds, classroomId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/classrooms/${classroomId}/quizzes/bulk`, {
            quizIds
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to bulk associate quizzes with classroom');
    }
});
// Bulk disassociate multiple quizzes from classrooms thunk
export const bulk_disassociate_quizzes_from_classrooms = createAsyncThunk('classroom/bulk_disassociate_quizzes', async ({ quizIds }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete('/quizzes/bulk/classroom', {
            data: { quizIds }
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to bulk disassociate quizzes from classrooms');
    }
});
// Initial state
const initialState = {
    classrooms: [],
    current_classroom: null,
    status: 'idle',
    error: null,
    createStatus: 'idle',
    createError: null,
    pending_enrollment_requests: [],
    soft_deleted_students: [],
    lesson_classrooms: {},
    quiz_template_classrooms: {}
};
// Create the classroom slice
const classroom_slice = createSlice({
    name: 'classroom',
    initialState,
    reducers: {
        clear_current_classroom: (state) => {
            state.current_classroom = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Create classroom cases
            .addCase(create_classroom.pending, (state) => {
            state.createStatus = 'loading';
            state.createError = null;
        })
            .addCase(create_classroom.fulfilled, (state, action) => {
            state.createStatus = 'succeeded';
            state.createError = null;
            state.error = null;
            const classroomWithArrays = {
                ...action.payload,
                students: action.payload.students || [],
                pending_enrollments: action.payload.pending_enrollments || [],
                lessons: action.payload.lessons || [],
                quizzes: action.payload.quizzes || [],
            };
            const idx = state.classrooms.findIndex((c) => c.id === classroomWithArrays.id);
            if (idx !== -1) {
                state.classrooms[idx] = classroomWithArrays;
            }
            else {
                state.classrooms.push(classroomWithArrays);
            }
            state.current_classroom = classroomWithArrays;
        })
            .addCase(create_classroom.rejected, (state, action) => {
            state.createStatus = 'failed';
            state.createError = action.payload || 'Failed to create classroom';
        })
            // Get classrooms by tutor cases
            .addCase(get_classrooms_by_tutor.pending, (state) => {
            state.error = null;
            if (state.classrooms.length === 0) {
                state.status = 'loading';
            }
            else if (state.status === 'failed') {
                // Silent retry while showing cached list
                state.status = 'succeeded';
            }
        })
            .addCase(get_classrooms_by_tutor.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.classrooms = action.payload.map((classroom) => ({
                ...classroom,
                students: classroom.students || [],
                pending_enrollments: classroom.pending_enrollments || [],
                lessons: classroom.lessons || [],
                quizzes: classroom.quizzes || [],
            }));
            state.error = null;
        })
            .addCase(get_classrooms_by_tutor.rejected, (state, action) => {
            if (action.payload === CLASSROOMS_LIST_FETCH_SUPERSEDED) {
                return;
            }
            state.status = 'failed';
            state.error = action.payload || 'Failed to fetch classrooms';
        })
            // Get classroom by ID cases
            .addCase(get_classroom_by_id.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(get_classroom_by_id.fulfilled, (state, action) => {
            state.status = 'succeeded';
            // Ensure the classroom has expected arrays
            const classroomWithArrays = {
                ...action.payload,
                students: action.payload.students || [],
                pending_enrollments: action.payload.pending_enrollments || [],
                lessons: action.payload.lessons || [],
                quizzes: action.payload.quizzes || []
            };
            state.current_classroom = classroomWithArrays;
            state.error = null;
            // Update in classrooms array if it exists
            const index = state.classrooms.findIndex(c => c.id === action.payload.id);
            if (index !== -1) {
                state.classrooms[index] = classroomWithArrays;
            }
        })
            .addCase(get_classroom_by_id.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'Failed to fetch classroom';
        })
            // Add student to classroom cases (now creates pending enrollment)
            .addCase(add_student_to_classroom.fulfilled, (state, action) => {
            if (state.current_classroom) {
                // Add to pending enrollments
                state.current_classroom.pending_enrollments.push(action.payload);
            }
        })
            // Update enrollment status cases
            .addCase(update_enrollment_status.fulfilled, (state, action) => {
            const { student_id, classroom_id, status } = action.payload;
            // Find the classroom
            const classroom = state.classrooms.find(c => c.id === classroom_id) || state.current_classroom;
            if (classroom) {
                // Update the status in pending_enrollments
                const pendingIndex = classroom.pending_enrollments.findIndex(e => e.student_id === student_id);
                if (pendingIndex !== -1) {
                    if (status === 'active') {
                        // Move from pending to active students if approved
                        const enrollment = classroom.pending_enrollments[pendingIndex];
                        if (enrollment.student) {
                            classroom.students.push(enrollment.student);
                        }
                        classroom.pending_enrollments.splice(pendingIndex, 1);
                    }
                    else {
                        // Just update the status
                        classroom.pending_enrollments[pendingIndex].status = status;
                    }
                }
            }
            // Also update in pending_enrollment_requests array
            const requestIndex = state.pending_enrollment_requests.findIndex(e => e.student_id === student_id && e.classroom_id === classroom_id);
            if (requestIndex !== -1) {
                if (status === 'active') {
                    state.pending_enrollment_requests.splice(requestIndex, 1);
                }
                else {
                    state.pending_enrollment_requests[requestIndex].status = status;
                }
            }
        })
            // Get pending enrollment requests cases
            .addCase(get_classroom_pending_requests.fulfilled, (state, action) => {
            state.pending_enrollment_requests = action.payload;
            // If we have a current classroom and the requests are for that classroom,
            // also update its pending_enrollments
            if (state.current_classroom && action.payload.length > 0) {
                const firstRequest = action.payload[0];
                if (firstRequest.classroom_id === state.current_classroom.id) {
                    state.current_classroom.pending_enrollments = action.payload;
                }
            }
        })
            // Add lesson to classroom cases
            .addCase(add_lesson_to_classroom.fulfilled, (state, action) => {
            if (state.current_classroom) {
                state.current_classroom.lessons.push(action.payload);
            }
        })
            // Add quiz to classroom cases
            .addCase(add_quiz_to_classroom.fulfilled, (state, action) => {
            if (state.current_classroom) {
                state.current_classroom.quizzes.push(action.payload);
            }
        })
            // Handle student enrolled classrooms
            .addCase(get_student_enrolled_classrooms.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(get_student_enrolled_classrooms.fulfilled, (state, action) => {
            state.status = 'succeeded';
            // Ensure all classrooms have the expected arrays
            state.classrooms = action.payload.map(classroom => ({
                ...classroom,
                students: classroom.students || [],
                pending_enrollments: classroom.pending_enrollments || [],
                lessons: classroom.lessons || [],
                quizzes: classroom.quizzes || []
            }));
            state.error = null;
        })
            .addCase(get_student_enrolled_classrooms.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'Failed to fetch enrolled classrooms';
        })
            // Handle student pending requests
            .addCase(get_student_pending_requests.fulfilled, (state, action) => {
            state.pending_enrollment_requests = action.payload;
        })
            // Association actions
            .addCase(associate_lesson_with_classroom.fulfilled, (state, action) => {
            // Refresh classroom lessons if we have a current classroom
            if (state.current_classroom && action.payload.success) {
                // The lesson will be added to the classroom lessons when we fetch them
            }
        })
            .addCase(disassociate_lesson_from_classroom.fulfilled, (state, action) => {
            // Remove lesson from current classroom if we have one
            if (state.current_classroom && action.payload.success) {
                // The lesson will be removed from the classroom lessons when we fetch them
            }
        })
            .addCase(associate_quiz_with_classroom.fulfilled, (state, action) => {
            // Refresh classroom quizzes if we have a current classroom
            if (state.current_classroom && action.payload.success) {
                // The quiz will be added to the classroom quizzes when we fetch them
            }
        })
            .addCase(disassociate_quiz_from_classroom.fulfilled, (state, action) => {
            // Remove quiz from current classroom if we have one
            if (state.current_classroom && action.payload.success) {
                // The quiz will be removed from the classroom quizzes when we fetch them
            }
        })
            .addCase(get_classroom_lessons.fulfilled, (state, action) => {
            if (state.current_classroom) {
                // Normalize: API returns lesson_id, map to id for compatibility; include visibility fields
                state.current_classroom.lessons = action.payload.map((l) => ({
                    ...l,
                    id: l.lesson_id ?? l.id
                }));
            }
        })
            .addCase(get_classroom_quizzes.fulfilled, (state, action) => {
            if (state.current_classroom) {
                state.current_classroom.quizzes = action.payload;
            }
        })
            .addCase(get_lesson_classrooms.fulfilled, (state, action) => {
            // Store lesson classrooms by lesson ID
            const lessonId = action.meta.arg.lessonId;
            state.lesson_classrooms[lessonId] = action.payload;
        })
            .addCase(get_lesson_classrooms.rejected, (state, action) => {
            // Mark as loaded so UI/effects do not retry GET in a loop when the request fails
            const lessonId = action.meta.arg.lessonId;
            state.lesson_classrooms[lessonId] = [];
        })
            .addCase(get_lesson_classrooms_batch.fulfilled, (state, action) => {
            // Merge batch result into lesson_classrooms
            const data = action.payload;
            for (const [lessonId, classrooms] of Object.entries(data)) {
                state.lesson_classrooms[lessonId] = classrooms;
            }
        })
            .addCase(get_quiz_template_classrooms_batch.fulfilled, (state, action) => {
            const data = action.payload;
            for (const [templateId, classroomIds] of Object.entries(data)) {
                state.quiz_template_classrooms[templateId] = classroomIds;
            }
        })
            .addCase(set_quiz_template_classrooms.fulfilled, (state, action) => {
            const { templateId } = action.meta.arg;
            const classroomIds = action.meta.arg.classroomIds;
            state.quiz_template_classrooms[templateId] = classroomIds;
        })
            // Soft delete student cases
            .addCase(soft_delete_student_from_classroom.fulfilled, (state, action) => {
            if (action.payload.success && state.current_classroom) {
                // Remove student from current classroom students list
                state.current_classroom.students = state.current_classroom.students.filter(student => student.id !== action.payload.student_id);
            }
        })
            // Restore student cases
            .addCase(restore_student_to_classroom.fulfilled, (state, action) => {
            if (action.payload.success) {
                // Student will be added back when we refresh the classroom data
                // For now, we could trigger a refetch of classroom students
            }
        })
            // Get soft-deleted students cases
            .addCase(get_soft_deleted_students.fulfilled, (state, action) => {
            state.soft_deleted_students = action.payload;
        });
    },
});
export const { clear_current_classroom } = classroom_slice.actions;
export default classroom_slice.reducer;
